import { useEffect, useRef, useState } from "react";
import { LuChevronRight } from "react-icons/lu";

export function SplitPanel({
  leftChild,
  rightChild,
  initialLeftChildWidth = 30,
  minLeftChildWidth = 100,
  minRightChildWidth = 100,
  onWidthChange,
}: {
  leftChild: React.ReactNode;
  rightChild: React.ReactNode;
  initialLeftChildWidth?: number;
  minLeftChildWidth?: number;
  minRightChildWidth?: number;
  onWidthChange?: (pixelWidth: number) => void;
}) {
  const [panelWidth, setPanelWidth] = useState(initialLeftChildWidth);
  const containerRef = useRef<HTMLDivElement>(null);
  const startXRef = useRef<number>(0);
  const startWidthRef = useRef<number>(0);
  const [isDragging, setIsDragging] = useState(false);
  const [isHandleHovered, setIsHandleHovered] = useState(false);

  const updatePanelWidth = () => {
    if (containerRef.current) {
      const containerWidth = containerRef.current.offsetWidth;
      const pixelWidth = (panelWidth / 100) * containerWidth;
      containerRef.current.style.setProperty(
        "--panel-width",
        `${pixelWidth}px`
      );
      // Notify parent of width change
      if (onWidthChange) {
        onWidthChange(pixelWidth);
      }
    }
  };

  useEffect(() => {
    if (!containerRef.current) return;

    const resizeObserver = new ResizeObserver(() => {
      updatePanelWidth();
    });

    resizeObserver.observe(containerRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, [panelWidth]);

  const handleStart = (clientX: number) => {
    if (!containerRef.current) return;

    startXRef.current = clientX;
    startWidthRef.current = panelWidth;
  };

  const handleMove = (clientX: number) => {
    if (!containerRef.current) return;

    const deltaX = clientX - startXRef.current;
    const containerWidth = containerRef.current.offsetWidth;
    const deltaPercent = (deltaX / containerWidth) * 100;
    const newWidth = Math.max(
      (minLeftChildWidth / containerWidth) * 100,
      Math.min(
        ((containerWidth - minRightChildWidth) / containerWidth) * 100,
        startWidthRef.current + deltaPercent
      )
    );

    setPanelWidth(newWidth);
  };

  useEffect(() => {
    if (!isDragging) return;

    const handleGlobalMouseMove = (e: MouseEvent) => {
      handleMove(e.clientX);
    };

    const handleGlobalMouseUp = () => {
      setIsDragging(false);
    };

    document.addEventListener("mousemove", handleGlobalMouseMove);
    document.addEventListener("mouseup", handleGlobalMouseUp);

    return () => {
      document.removeEventListener("mousemove", handleGlobalMouseMove);
      document.removeEventListener("mouseup", handleGlobalMouseUp);
    };
  }, [isDragging]);

  const changeWidth = (e: React.MouseEvent) => {
    e.preventDefault();
    handleStart(e.clientX);
    setIsDragging(true);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    handleStart(e.touches[0].clientX);

    const handleTouchMove = (e: Event) => {
      const touchEvent = e as TouchEvent;
      handleMove(touchEvent.touches[0].clientX);
    };

    const handleTouchEnd = () => {
      document.removeEventListener("touchmove", handleTouchMove);
      document.removeEventListener("touchend", handleTouchEnd);
    };

    document.addEventListener("touchmove", handleTouchMove);
    document.addEventListener("touchend", handleTouchEnd);
  };

  return (
    <div className="flex h-full w-full relative" ref={containerRef}>
      <div
        className="h-full w-full"
        style={{
          width: `var(--panel-width, 100%)`,
          minWidth: `${minLeftChildWidth}px`,
        }}
      >
        {leftChild}
      </div>

      <div
        className="w-px cursor-col-resize touch-none z-20
        bg-white/50 dark:bg-dark-border relative"
        onMouseDown={changeWidth}
        onTouchStart={handleTouchStart}
        onMouseEnter={() => setIsHandleHovered(true)}
        onMouseLeave={() => setIsHandleHovered(false)}
      >
        <div
          className={`absolute top-1/2 left-0 -translate-y-1/2
          bg-white/50 dark:bg-[#2A2E34]
          py-7 rounded-tr-2xl rounded-br-2xl
          transition-opacity duration-300
          ${isDragging || isHandleHovered ? "opacity-100" : "opacity-[40%]"}`}
        >
          <LuChevronRight size={16} />
        </div>
      </div>

      <div
        className="h-full w-full"
        style={{
          width: `calc(100% - var(--panel-width, 40%))`,
          minWidth: `${minRightChildWidth}px`,
        }}
      >
        {rightChild}
      </div>
    </div>
  );
}
